"use client";

import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { API_BASE, fetchWithAuth } from "@/lib/auth";
import { windowParams, type InsightsFilters } from "./filters";

export class InsightsError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

type Params = Record<string, string | number | boolean | null | undefined>;

export function toQuery(params: Params): string {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== "") q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : "";
}

export async function insightsFetch<T>(path: string, params: Params = {}, init?: RequestInit): Promise<T> {
  const res = await fetchWithAuth(`${API_BASE}/api/v1/insights${path}${toQuery(params)}`, init);
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = typeof body?.detail === "string" ? body.detail : JSON.stringify(body?.detail ?? body);
    } catch { /* not JSON */ }
    throw new InsightsError(res.status, detail || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

/** A window-scoped GET. The query key includes every filter that changes the answer. */
export function useInsightsQuery<T>(
  path: string,
  filters: InsightsFilters,
  extra: Params = {},
  options: { enabled?: boolean; windowed?: boolean } = {},
) {
  const { enabled = true, windowed = true } = options;
  const params = { ...(windowed ? windowParams(filters) : {}), ...extra };
  return useQuery<T, InsightsError>({
    queryKey: ["insights", path, params],
    queryFn: () => insightsFetch<T>(path, params),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: (count, err) => count < 1 && (err?.status ?? 500) >= 500,
  });
}

/** Cursor-paged GET (person timeline, raw API calls). */
export function useInsightsInfinite<T extends { next_cursor?: string | null }>(
  path: string,
  filters: InsightsFilters,
  extra: Params = {},
  cursorParam = "before",
  enabled = true,
) {
  const params = { ...windowParams(filters), ...extra };
  return useInfiniteQuery<T, InsightsError>({
    queryKey: ["insights", path, params, "infinite"],
    queryFn: ({ pageParam }) => insightsFetch<T>(path, { ...params, [cursorParam]: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
    staleTime: 60_000,
  });
}
